import React from "react";
import { NewSelect, useViewport } from "@bca/ui-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getContactInformation } from "../../reducers/contactInformationSlice";
import {
  StyledNewSelect,
  StyledContactInformation,
  StyledContent,
  StyledTitle,
  StyledSection,
  StyledInputGroup,
  StyledInput,
} from "./ContactInformation.styled";
import Button from "../../components/Button/Button";
import WebStepper from "../../components/WebStepper/WebStepper";
import {
  changeEmail,
  changeFirstName,
  changeLastName,
  changeMiddleName,
  changePhoneNumber,
  changePhoneNumberSelect,
  changeSalutation,
} from "../../reducers/formSlice";
import fetchMobileStepper from "../../utils/fetchMobileStepper";
import CircularStepper from "../../components/CircularStepper/CircularStepper";
import { nameValidation } from "../../constants/nameValidation";
import {
  COMPANY_DETAILS_PATH,
  CONTACT_INFORMATION_PATH,
  HOME_PATH,
} from "../../constants/paths";
import { setShowServerErrorMessage } from "../../reducers/errorManagementSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const ContactInformation = () => {
  const { page_content, loading } = useSelector(
    (state) => state.contactInformationScreen
  );

  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );

  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const dispatch = useDispatch();

  const [contactInformation, setContactInformation] = React.useState({
    salutation: formState.salutation.value || "",
    firstName: formState.first_name.value || "",
    middleName: formState.middle_name.value || "",
    lastName: formState.last_name.value || "",
    phoneNumberSelect: formState.phone_number_select.value || "",
    phoneNumber: formState.phone_number.value || "",
    email: formState.email.value || "",
  });

  const [continueButtonDisabled, setContinueButtonDisabled] =
    React.useState(true);

  const [mobileStepper, setMobileStepper] = React.useState();

  const [emailVerified, setEmailVerified] = React.useState(true);

  const [phoneNumberVerified, setPhoneNumberVerified] = React.useState(true);

  const [firstNameVerified, setFirstNameVerified] = React.useState(true);

  const [middleNameVerified, setMiddleNameVerified] = React.useState(true);

  const [lastNameVerified, setLastNameVerified] = React.useState(true);

  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  React.useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(getContactInformation(countrySelected, languageCode));

    const getResult = async () => {
      const result = await fetchMobileStepper(languageCode, setMobileStepper);
      dispatch(setShowServerErrorMessage(result.responseStatus));
    };
    getResult();
  }, [countrySelected, dispatch, languageCode]);

  React.useEffect(() => {
    if (
      (page_content?.salutation?.required === true
        ? formState.salutation.value !== ""
        : true) &&
      (page_content?.first_name?.required === true
        ? contactInformation.firstName !== ""
        : true) &&
      (page_content?.last_name?.required === true
        ? contactInformation.lastName !== ""
        : true) &&
      (page_content?.phone_number_select?.required === true
        ? formState.phone_number_select.value !== ""
        : true) &&
      (page_content?.phone_number?.required === true
        ? contactInformation.phoneNumber !== ""
        : true) &&
      (page_content?.email?.required === true
        ? contactInformation.email !== ""
        : true) &&
      firstNameVerified &&
      middleNameVerified &&
      lastNameVerified &&
      emailVerified &&
      phoneNumberVerified
    ) {
      setContinueButtonDisabled(false);
    } else {
      setContinueButtonDisabled(true);
    }
  }, [
    contactInformation,
    emailVerified,
    firstNameVerified,
    formState.phone_number_select.value,
    formState.salutation.value,
    lastNameVerified,
    middleNameVerified,
    page_content,
    phoneNumberVerified,
  ]);

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "3",
          stepName: "step 3 contact information",
        },
        pageInfo: {
          pageTitle: "Contact Information",
          pagePath: `/${CONTACT_INFORMATION_PATH}`,
          previousPagePath: `/${COMPANY_DETAILS_PATH}`,
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "3",
          stepName: "step 3 contact information",
        },
        pageInfo: {
          pageTitle: "Contact Information",
          pagePath: `/${CONTACT_INFORMATION_PATH}`,
          previousPagePath: `/${COMPANY_DETAILS_PATH}`,
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode]);

  return (
    <StyledContactInformation>
      {!loading && (
        <>
          <StyledContent>
            {!isMobile && (
              <>
                <WebStepper activeStep={2} />
                <StyledTitle variant="h1">{page_content.header}</StyledTitle>
              </>
            )}
            {isMobile && (
              <CircularStepper
                mobileStepper={mobileStepper}
                activeStepIndex={2}
              />
            )}
            <StyledSection>
              <StyledInputGroup>
                <StyledNewSelect
                  data-cy="contact-information-salutation"
                  value={formState.salutation.value}
                  label={page_content?.salutation?.label}
                  onChange={(e) => dispatch(changeSalutation(e.target.value))}
                >
                  {page_content?.salutation?.options?.map((option) => {
                    return (
                      <NewSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </NewSelect.Option>
                    );
                  })}
                </StyledNewSelect>

                <StyledInput
                  data-cy="contact-information-first-name"
                  state={firstNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    firstNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.first_name?.label}
                  required={page_content?.first_name?.required}
                  value={contactInformation.firstName}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);

                    setContactInformation((prevState) => ({
                      ...prevState,
                      firstName: e.target.value,
                    }));

                    if (e.target.value !== "") {
                      setFirstNameVerified(regex.test(e.target.value));
                    } else {
                      setFirstNameVerified(false);
                    }
                  }}
                  onBlur={(e) => dispatch(changeFirstName(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="contact-information-middle-name"
                  state={middleNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    middleNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.middle_name?.label}
                  required={page_content.middle_name?.required}
                  value={contactInformation.middleName}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);
                    setContactInformation((prevState) => ({
                      ...prevState,
                      middleName: e.target.value,
                    }));

                    if (e.target.value !== "") {
                      setMiddleNameVerified(regex.test(e.target.value));
                    } else {
                      setMiddleNameVerified(true);
                    }
                  }}
                  onBlur={(e) => dispatch(changeMiddleName(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="contact-information-last-name"
                  state={lastNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    lastNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.last_name?.label}
                  required={page_content?.last_name?.required}
                  value={contactInformation.lastName}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);

                    setContactInformation((prevState) => ({
                      ...prevState,
                      lastName: e.target.value,
                    }));

                    if (e.target.value !== "") {
                      setLastNameVerified(regex.test(e.target.value));
                    } else {
                      setLastNameVerified(false);
                    }
                  }}
                  onBlur={(e) => dispatch(changeLastName(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledNewSelect
                  data-cy="contact-information-phone-number-select"
                  value={formState.phone_number_select.value}
                  label={page_content?.phone_number_select?.label}
                  onChange={(e) =>
                    dispatch(changePhoneNumberSelect(e.target.value))
                  }
                >
                  {page_content?.phone_number_select?.options?.map((option) => {
                    return (
                      <NewSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </NewSelect.Option>
                    );
                  })}
                </StyledNewSelect>

                <StyledInput
                  data-cy="contact-information-phone-number"
                  state={phoneNumberVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    phoneNumberVerified
                      ? ""
                      : page_content?.phone_number_error_message_block
                  }
                  label={page_content?.phone_number?.label}
                  required={page_content?.phone_number?.required}
                  value={contactInformation.phoneNumber}
                  onChange={(e) => {
                    let regex;
                    setContactInformation((prevState) => ({
                      ...prevState,
                      phoneNumber: e.target.value,
                    }));

                    if (page_content?.phone_number_validation_block) {
                      regex = new RegExp(
                        page_content?.phone_number_validation_block
                      );
                    }

                    if (e.target.value !== "") {
                      setPhoneNumberVerified(regex.test(e.target.value));
                    } else {
                      setPhoneNumberVerified(false);
                    }
                  }}
                  onBlur={(e) => dispatch(changePhoneNumber(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="contact-information-email"
                  state={emailVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    emailVerified ? "" : page_content?.email_error_message
                  }
                  label={page_content?.email?.label}
                  required={page_content?.email?.required}
                  value={contactInformation.email}
                  onChange={(e) => {
                    let regex;

                    setContactInformation((prevState) => ({
                      ...prevState,
                      email: e.target.value,
                    }));

                    if (page_content?.email_validation) {
                      regex = new RegExp(page_content?.email_validation);
                    }

                    if (e.target.value !== "") {
                      setEmailVerified(regex.test(e.target.value));
                    } else {
                      setEmailVerified(false);
                    }
                  }}
                  onBlur={(e) => dispatch(changeEmail(e.target.value))}
                />
              </StyledInputGroup>
            </StyledSection>
          </StyledContent>
          <Button
            backButtonText={page_content?.back_button?.label}
            backLink="#"
            backButtonDisabled={page_content?.back_button?.disable}
            continueButtonText={page_content?.continue_button?.label}
            nextLink="/final-details"
            nextButtonDisabled={continueButtonDisabled}
            onBackClick={() => navigate(-1)}
            isMobile={vp.lessThan.md}
          />
        </>
      )}
    </StyledContactInformation>
  );
};

export default ContactInformation;
